<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import LogoReferences from "@components/LogoReferences.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import ReferenceContents from "@components/ReferenceContents.svelte";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { PartnerPlacement } from "@core/schema/PartnerPlacement.js";
	import { ReferencePlacement } from "@core/schema/ReferencePlacement.js";
	import { getClientsPath } from "@core/schema/paths/getClientsPath.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getDigitalizationAndAutomationPath } from "@core/schema/paths/getDigitalizationAndAutomationPath.js";
	import { getEcommercePath } from "@core/schema/paths/getEcommercePath.js";
	import { getReferencePath } from "@core/schema/paths/getReferencePath.js";
	import { getWebsitesPath } from "@core/schema/paths/getWebsitesPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getCoopStepByStepProduction } from "../data/getCoopStepByStepProduction.js";
	import { getFaqsProduction } from "../data/getFaqsProduction.js";
	import { getPartnerLogos } from "../data/getPartnerLogos.js";
	import { getProblemsSolvedProduction } from "../data/getProblemsSolvedProduction.js";
	import type { ProductionProps } from "./ProductionProps.js";

	const { tenant } = getGlobalTemplateProps();
	const { partners }: ProductionProps = $props();
</script>

<Layout>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="Digitalizujeme, zrychlíme nebo zpřehledníme cg[[vaši výrobu]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Aby výroba běžela hladce, musí všechno šlapat jako dobře namazaný stroj. Pomůžeme vám sp[[zautomatizovat]] zdlouhavé procesy, dodáme sp[[docházkový systém]] i jinou interní aplikaci na míru nebo pro vás vytvoříme moderní [{getWebsitesPath(
						tenant,
					)}][[web]] či [{getEcommercePath(tenant)}][[e‑shop]] podle vašich potřeb."
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Nezávazně poptat" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section gridHeadline="container-8" gridDesc="container-6" centered>
		{#snippet headline()}
			<Formatted text="cg[[Zefektivňujeme]] výrobní procesy pomocí systémů, aplikací i webů na míru" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Máte sp[[chaos v objednávkách]], zadáváte sp[[ručně]] data do tabulek nebo sp[[ztrácíte přehled]] o docházce zaměstnanců? Tyto a podobné problémy pravidelně sp[[řešíme]] pro klienty (nejen) z oblasti výroby. "
				/>
			</p>
			<p>
				<Formatted
					text="sp[[Vyvineme]] pro vás přehledný interní systém na míru, [{getDigitalizationAndAutomationPath(
						tenant,
					)}][[digitalizujeme]] zdlouhavé procesy nebo propojíme vaše stávající systémy a sp[[pomůžeme vám tak ušetřit čas i náklady]]."
				/>
			</p>
		{/snippet}
	</Section>
	<Section
		background={Background.LightGradient}
		paddingFollowNext
		gridHeadline="container-8"
		gridDesc="container-6"
		centered
	>
		{#snippet headline()}
			<Formatted text="Příklady cg[[problémů]], které řešíme ve výrobních firmách" />
		{/snippet}
	</Section>

	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getProblemsSolvedProduction(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} indexNumber={index + 1} />
			{/each}
		</div>
	</Section>

	<Link
		variant={ButtonVariant.Phlox}
		class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
		>Zeptat se na podrobnosti
		<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
	</Link>

	<Section
		noPadding="bot"
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Software a další řešení cg[[na míru]] děláme pro výrobní firmy napříč obory" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Nezáleží, jestli provozujete cukrářský podnik na maloměstě nebo provozujete velkou průmyslovou výrobu. sp[[Zefektivňovat]] procesy pomáháme výrobním podnikům z sp[[různých oborů]]."
				/>
			</p>
			<div class="flex flex-col md:flex-row md:gap-16">
				<ul class="custom-list">
					<li><Formatted text="Strojírenství a kovovýroba" /></li>
					<li><Formatted text="Výrobci nábytku" /></li>
					<li><Formatted text="Potravinářství a nápoje" /></li>
					<li><Formatted text="Stavebnictví" /></li>
					<li><Formatted text="Textilní výroba" /></li>
					<li><Formatted text="Tiskařské firmy" /></li>
				</ul>
			</div>
		{/snippet}
	</Section>
	<Section gridHeadline="container-8" gridDesc="container-6" centered>
		{#snippet headline()}
			<Formatted text="Vývojem na cloudu vám cg[[ušetříme]] až desítky tisíc" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Víte, že vaše výrobní firma sp[[nemusí]] kvůli provozování interních systémů a dalšího softwaru sp[[vyhazovat peníze]] za servery a další hardware?"
				/>
			</p>
			<p>
				<Formatted
					text="Vyvíjíme sp[[primárně na cloudu]] (nejčastěji využíváme Amazon Web Services), čímž vám ušetříme až desítky tisíc Kč. Provoz softwaru je navíc výrazně sp[[bezpečnější]] než na fyzických serverech ve firmě."
				/>
			</p>
		{/snippet}
	</Section>

	<Section background={Background.LightGradient} centered gridHeadline="container-6" noPadding="bot">
		{#snippet headline()}
			<Formatted text="Komu jsme pomohli cg[[zefektivnit]] procesy nebo cg[[zrychlit]] výrobu" />
		{/snippet}
		<div class="mt-12">
			<ReferenceContents limit={8} placement={ReferencePlacement.Production} />
		</div>
		<Link
			variant={ButtonVariant.GreenOutlineLightBg}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getReferencePath(tenant)}
			>Zobrazit více
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>
	<LogoReferences logos={getPartnerLogos(partners, PartnerPlacement.Production)} />
	{@const contacts = getBookMeetingPeople(tenant)}

	<Link variant={ButtonVariant.GreenOutlineLightBg} href={getClientsPath(tenant)} class="mx-auto mt-2 sm:mt-4">
		<span class="hidden md:inline-block">Zobrazit úspěšné projekty</span>
	</Link>

	<Section
		paddingFollowNext
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="Jak cg[[spolupracujeme]] s výrobními firmami" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getCoopStepByStepProduction(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}
	</Section>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Často kladené otázky k řešením v cg[[oblasti výroby]]" />
		{/snippet}
		<FaqItems faqs={getFaqsProduction(tenant)} />
	</Section>
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera]]} />
</Layout>
